import { timestampDate } from '@bufbuild/protobuf/wkt'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type AutoExecutionHistoryEntry,
  AutoExecutionNoteReason,
  AutoExecutionResult,
} from '@ebalance/proto/bffuser/v1/workflow_automation_history_model_pb'
import { DemandAutoConfirmMethod } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'
import { formatCivilDateSlash } from '~/models/civilDate'
import { formatDateTimeFull } from '~/models/dateTime'

/** 記録の無い項目の表示 (AC-AEH-006)。 */
export const AUTO_EXECUTION_HISTORY_NO_VALUE = '—'

export type AutoExecutionResultTone =
  | 'positive'
  | 'warning'
  | 'neutral'
  | 'negative'

export type AutoExecutionHistoryRowView = {
  key: string
  executedAt: string
  workflow: string
  area: string
  targetDate: string
  subStep: string
  method: string
  result: { label: string; tone: AutoExecutionResultTone }
  /** 1 備考 1 行。 無ければ空配列 (表示側が値なしを出す)。 */
  notes: string[]
}

type KnownResult = Exclude<AutoExecutionResult, AutoExecutionResult.UNSPECIFIED>
type KnownReason = Exclude<
  AutoExecutionNoteReason,
  AutoExecutionNoteReason.UNSPECIFIED
>

// proto enum の網羅 Record。 値を足すと型 check で落ちる (Note「caller に残る複雑さ」)。
const RESULT_VIEW: Record<
  KnownResult,
  { labelKey: string; tone: AutoExecutionResultTone }
> = {
  [AutoExecutionResult.EXECUTED]: {
    labelKey: 'page:autoExecutionHistory.result.executed',
    tone: 'positive',
  },
  [AutoExecutionResult.AWAITING_APPROVAL]: {
    labelKey: 'page:autoExecutionHistory.result.awaitingApproval',
    tone: 'warning',
  },
  [AutoExecutionResult.APPROVAL_HELD]: {
    labelKey: 'page:autoExecutionHistory.result.approvalHeld',
    tone: 'warning',
  },
  [AutoExecutionResult.SKIPPED]: {
    labelKey: 'page:autoExecutionHistory.result.skipped',
    tone: 'neutral',
  },
  [AutoExecutionResult.FAILED]: {
    labelKey: 'page:autoExecutionHistory.result.failed',
    tone: 'negative',
  },
}

const REASON_LABEL_KEY: Record<KnownReason, string> = {
  [AutoExecutionNoteReason.DEMAND_BG_NOT_FOUND]:
    'page:autoExecutionHistory.reason.demandBgNotFound',
  [AutoExecutionNoteReason.METHOD_UNDECIDABLE]:
    'page:autoExecutionHistory.reason.methodUndecidable',
  [AutoExecutionNoteReason.NO_ACTUALS_ALL_ZERO]:
    'page:autoExecutionHistory.reason.noActualsAllZero',
  [AutoExecutionNoteReason.NOT_COMPLETED]:
    'page:autoExecutionHistory.reason.notCompleted',
  [AutoExecutionNoteReason.PAST_DELIVERY_DATE]:
    'page:autoExecutionHistory.reason.pastDeliveryDate',
}

function monthDay(d: ProtoDate): string {
  return `${d.month}/${d.day}`
}

function noteText(
  reason: AutoExecutionNoteReason,
  dates: readonly ProtoDate[],
  t: TFunction,
): string {
  if (reason === AutoExecutionNoteReason.UNSPECIFIED) {
    return AUTO_EXECUTION_HISTORY_NO_VALUE
  }
  const label = t(REASON_LABEL_KEY[reason])
  // dates が空 = 全日 (日付を付けない)
  if (dates.length === 0) return label
  return `${dates.map(monthDay).join(t('common:filter.listSeparator'))}: ${label}`
}

function targetDateText(entry: AutoExecutionHistoryEntry): string {
  const first = entry.targetFirstDate
  const last = entry.targetLastDate
  if (first === undefined) return AUTO_EXECUTION_HISTORY_NO_VALUE
  const firstText = formatCivilDateSlash(first)
  if (
    last === undefined ||
    (last.year === first.year &&
      last.month === first.month &&
      last.day === first.day)
  ) {
    return firstText
  }
  return `${firstText}〜${formatCivilDateSlash(last)}`
}

function methodText(entry: AutoExecutionHistoryEntry, t: TFunction): string {
  const method = entry.method?.method
  if (
    method?.case !== 'demandForecast' ||
    method.value === DemandAutoConfirmMethod.UNSPECIFIED
  ) {
    return AUTO_EXECUTION_HISTORY_NO_VALUE
  }
  return t(
    `page:adminWorkflowTemplates.subStep.forecastMethods.${method.value}`,
  )
}

/** 1 行 8 列の表示値。 pure (AC-AEH-006)。 */
export function toAutoExecutionHistoryRowView(
  entry: AutoExecutionHistoryEntry,
  t: TFunction,
): AutoExecutionHistoryRowView {
  const target =
    entry.target.case === 'subStep' ? entry.target.value : undefined
  const result =
    entry.result === AutoExecutionResult.UNSPECIFIED
      ? { label: AUTO_EXECUTION_HISTORY_NO_VALUE, tone: 'neutral' as const }
      : {
          label: t(RESULT_VIEW[entry.result].labelKey),
          tone: RESULT_VIEW[entry.result].tone,
        }
  return {
    key: entry.logId,
    executedAt:
      entry.executedAt === undefined
        ? AUTO_EXECUTION_HISTORY_NO_VALUE
        : formatDateTimeFull(timestampDate(entry.executedAt)),
    workflow:
      target === undefined || target.workflowId === ''
        ? AUTO_EXECUTION_HISTORY_NO_VALUE
        : target.workflowId,
    area:
      target === undefined || target.areaCode === AreaCode.UNSPECIFIED
        ? AUTO_EXECUTION_HISTORY_NO_VALUE
        : convertAreaLabel(target.areaCode),
    targetDate: targetDateText(entry),
    subStep:
      target?.subStepName === undefined || target.subStepName === ''
        ? AUTO_EXECUTION_HISTORY_NO_VALUE
        : target.subStepName,
    method: methodText(entry, t),
    result,
    notes: entry.notes.map((n) => noteText(n.reason, n.dates, t)),
  }
}
